import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Zap, TrendingUp, Star } from 'lucide-react';
import { COLORS, getTimeHorizonStyle } from '../utils/colors';
import { getTrendUrl } from '../utils/slugs';

// Modern, responsive trend card for both landing page and search results
const TrendCard = ({ trend, onSelect, layout = 'grid' }) => {
  const timeHorizonStyle = getTimeHorizonStyle(trend.timeHorizon);
  
  // Map maturity level to color and text
  const getMaturityIndicator = (maturity) => {
    if (maturity < 30) return { color: '#EF4444', text: 'Emerging' };
    if (maturity < 60) return { color: '#F59E0B', text: 'Growing' };
    return { color: '#10B981', text: 'Maturing' };
  };
  
  const maturityIndicator = getMaturityIndicator(trend.maturity);
  
  // Handle card click
  const handleClick = () => {
    if (onSelect) {
      onSelect(trend.id);
    }
  };
  
  // Render grid style card (default)
  if (layout === 'grid') {
    return (
      <div 
        className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 cursor-pointer transform hover:translate-y-[-4px] h-full flex flex-col"
        onClick={handleClick}
      >
        <div className="p-5 flex-1 flex flex-col">
          <div className="flex flex-wrap gap-2 justify-between items-start mb-3">
            <span className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap" 
              style={{ backgroundColor: `${COLORS.primary}15`, color: COLORS.primary }}>
              {trend.category}
            </span>
            <span className={`${timeHorizonStyle} rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1 whitespace-nowrap`}>
              <Clock className="w-3 h-3" />
              {trend.timeHorizon}
            </span>
          </div>
          
          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 line-clamp-2">{trend.title}</h3>
          
          {trend.subtitle && (
            <p className="text-gray-500 text-sm mb-2 italic line-clamp-1">{trend.subtitle}</p>
          )}
          
          <p className="text-gray-600 mb-4 text-sm line-clamp-3">{trend.description}</p>
          
          <div className="mt-auto space-y-4 mb-4">
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>Maturity</span>
                </span>
                <div className="flex items-center gap-1">
                  <span>{maturityIndicator.text}</span>
                  <span className="font-mono">{trend.maturity}%</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full" 
                  style={{
                    width: `${trend.maturity}%`,
                    backgroundColor: maturityIndicator.color
                  }}
                ></div>
              </div>
            </div>
            
            {trend.focusArea && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Star className="w-3 h-3" />
                <span>Focus: {trend.focusArea}</span>
              </div>
            )}
          </div>
          
          <Link 
            to={getTrendUrl(trend)}
            className="inline-flex items-center gap-1 px-4 py-2 text-white rounded-lg text-sm font-medium transition-all hover:shadow-md"
            style={{ backgroundColor: COLORS.primary }}
            onClick={(e) => e.stopPropagation()}
          >
            <span>View Trend</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }
  
  // Render list style card (more horizontal for better use of space)
  return (
    <div 
      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 cursor-pointer transform hover:translate-y-[-2px]"
      onClick={handleClick}
    >
      <div className="p-5 flex flex-col md:flex-row md:items-center">
        <div className="flex-1">
          <div className="flex flex-wrap gap-2 items-center mb-2">
            <span className="px-3 py-1 rounded-full text-xs font-medium" 
              style={{ backgroundColor: `${COLORS.primary}15`, color: COLORS.primary }}>
              {trend.category}
            </span>
            <span className={`${timeHorizonStyle} rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1`}>
              <Clock className="w-3 h-3" />
              {trend.timeHorizon}
            </span>
            
            {trend.focusArea && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Zap className="w-3 h-3" />
                <span>{trend.focusArea}</span>
              </div>
            )}
          </div>
          
          <h3 className="text-xl font-bold text-gray-900 mb-1">{trend.title}</h3>
          <p className="text-gray-600 mb-3 text-sm line-clamp-2">{trend.description}</p>
          
          <div className="flex items-center gap-4 mb-3 md:mb-0">
            <div className="flex-1 max-w-xs">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Maturity</span>
                <span>{trend.maturity}%</span>
              </div>
              <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full" 
                  style={{
                    width: `${trend.maturity}%`,
                    backgroundColor: maturityIndicator.color
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-3 md:mt-0 md:ml-4">
          <Link 
            to={getTrendUrl(trend)}
            className="inline-flex items-center gap-1 px-4 py-2 text-white rounded-lg text-sm font-medium transition-all hover:shadow-md"
            style={{ backgroundColor: COLORS.primary }}
            onClick={(e) => e.stopPropagation()}
          >
            <span>View Trend</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TrendCard;